/**
 * Pie Chart Widget - Muestra datos agrupados en gráfico circular (donut)
 *
 * Widget para mostrar métricas agrupadas con total en el centro.
 */

import React from 'react';
import { Box, Typography, Stack, useTheme } from '@mui/material';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { WidgetWrapper } from './WidgetWrapper';
import {useChartPalette} from "../../../theme/chartPalette";

export interface PieChartData {
  groupKey: string | null;
  groupLabel?: string;
  count: number;
}

export interface PieChartProps {
  title: string;
  data: PieChartData[];
  loading?: boolean;
  computing?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  icon?: React.ReactNode;
  showLegend?: boolean;
  maxItems?: number;
}

// Colores para los segmentos (Material Design palette)

export const PieChart: React.FC<PieChartProps> = ({
  title,
  data,
  loading = false,
  computing = false,
  error = null,
  onRefresh,
  icon,
  showLegend = true,
  maxItems = 10,
}) => {
  const theme = useTheme();
  const paleta = useChartPalette();

  // Limitar items y calcular total
  const displayData = data.slice(0, maxItems);
  const total = data.reduce((sum, item) => sum + item.count, 0);

  // Preparar datos para recharts
  const chartData = displayData.map((item, index) => ({
    name: getLabel(item),
    value: item.count,
    color: paleta.color(index),
    percentage: total > 0 ? ((item.count / total) * 100).toFixed(1) : '0',
  }));

  function getLabel(item: PieChartData): string {
    if (item.groupLabel) return item.groupLabel;
    if (item.groupKey === null) return '(Sin valor)';
    return String(item.groupKey);
  }

  // Tooltip personalizado
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1.5,
            borderRadius: 1,
            boxShadow: theme.shadows[3],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            {data.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.value.toLocaleString()} ({data.percentage}%)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <WidgetWrapper
      title={title}
      loading={loading}
      computing={computing}
      error={error}
      onRefresh={onRefresh}
      icon={icon}
      minHeight={280}
    >
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {displayData.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No hay datos disponibles
            </Typography>
          </Box>
        ) : (
          <>
            {/* Chart container */}
            <Box sx={{ position: 'relative', height: 180, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </RechartsPie>
              </ResponsiveContainer>

              {/* Total en el centro */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="text.primary"
                  lineHeight={1}
                >
                  {total.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
              </Box>
            </Box>

            {/* Legend */}
            {showLegend && (
              <Box sx={{ px: 2, pt: 1, maxHeight: 120, overflow: 'auto' }}>
                <Stack spacing={0.5}>
                  {chartData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: item.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {item.value.toLocaleString()} ({item.percentage}%)
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {data.length > maxItems && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    +{data.length - maxItems} más
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </WidgetWrapper>
  );
};

export default PieChart;
